import React, { useEffect, useState } from "react";
import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Paper,
  Button,
  TablePagination,
  useMediaQuery,
} from "@mui/material";
import SortIcon from "@mui/icons-material/Sort";
import { CircularLoader } from "../widgets/CircularLoader";
import './CustomTable.css';

const CustomTable = (props) => {
  const {
    columns,
    data,
    actions = [],
    rowsPerPageOptions = [5, 10, 15],
    actionsHeader = "Actions",
    isLoading,
    currentPage,
    totalLength,
    handlePageChange,
    handlePageSize,
    handleTableSorting,
  } = props;

  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [maxTableHeight, setMaxTableHeight] = useState("500px");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const hasActions = actions && actions.length > 0;

  useEffect(() => {
    const calculateMaxTableHeight = () => {
      const offset = 220;
      setMaxTableHeight(`calc(100vh - ${offset}px)`);
    };

    calculateMaxTableHeight();
    window.addEventListener("resize", calculateMaxTableHeight);
    return () => window.removeEventListener("resize", calculateMaxTableHeight);
  }, []);

  const handleChangeRowsPerPage = (event) => {
    const pageSize = parseInt(event.target.value);
    setRowsPerPage(pageSize);
    handlePageSize(pageSize);
  };

  const handleSort = (columnKey) => {
    setSortConfig((prev) => {
      let newConfig;
      if (prev.key === columnKey) {
        newConfig = {
          key: columnKey,
          direction: prev.direction === "asc" ? "desc" : null,
        };
      } else {
        newConfig = { key: columnKey, direction: "asc" };
      }
      if (!newConfig.direction) newConfig.key = null;
      handleTableSorting(newConfig.key, newConfig.direction);
      return newConfig;
    });
  };

  if (isLoading) return <CircularLoader />;

  return (
    <div className="custom-table-wrapper">
     <TableContainer component={Paper} sx={{
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        borderRadius: '20px',
        overflow: 'auto',
        maxHeight: '300px',
        margin: '20px 0',
        transition: 'all 0.3s ease',
         '&::-webkit-scrollbar': { width: '6px', height: '2px' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '10px' },
        '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1' },
        '&:hover': { transform: 'scale(1.02)', boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.2)' },
        '@media (max-width: 319px)': { width: '80%' },
        '@media (min-width: 320px) and (max-width: 480px)': { width: '90%' },
        '@media (min-width: 481px) and (max-width: 768px)': { width: '90%' },
        '@media (min-width: 769px) and (max-width: 1024px)': { width: '90%' },
      }}>
        <Table stickyHeader>
          <TableHead sx={{ backgroundColor: '#1565c0' }}>
            <TableRow sx={{ height: '30px', transition: 'all 0.2s ease', '&:hover': { backgroundColor: '#ffffff' },fontFamily: "'DM Sans', sans-serif", }}>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  sx={{ backgroundColor: 'white',color: 'black',fontFamily:"Roboto"
                  , fontSize: '18px'}}
                >
                  {column.sorting ? (
                    <TableSortLabel
                      active={sortConfig.key === column.key}
                      direction={
                        sortConfig.key === column.key
                          ? sortConfig.direction
                          : "asc"
                      }
                      onClick={() => handleSort(column.key)}
                      IconComponent={SortIcon}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
              {hasActions && (
                <TableCell
                style={{ backgroundColor: 'white',color: 'black',  fontFamily:"Roboto", fontSize: '18px'}}
                >
                  {actionsHeader}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody style={{ backgroundColor: 'white' }}>
            {data?.length > 0 &&
              data.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{transition: 'all 0.2s ease', '&:hover': { backgroundColor: '#ffffff' },  fontFamily:"Roboto"                  , fontSize: '16px' }}
                >
                  {columns.map((column, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        fontSize: isMobile ? "11px" : "16px",
                        padding: isMobile ? "10px 8px" : "14px 16px",
                        color: "#333",
                      }}
                    >
                      {column.format
                        ? column.format(row[column.key] ?? "")
                        : row[column.key] ?? ""}
                    </TableCell>
                  ))}
                  {hasActions && (
                    <TableCell>
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          flexWrap: "wrap",
                          justifyContent: isMobile ? "center" : "flex-start",
                        }}
                      >
                        {actions.map((action, i) => (
                          <Button
                            key={i}
                            onClick={() => action.onClick(row)}
                            variant="contained"
                            style={{ textTransform: 'none' ,borderRadius:"6px",    fontFamily:"Roboto"  ,backgroundColor:"#8b003c",color:"white",'&:hover': {backgroundColor: '#8b003c', // keep background same
                              color: 'white',    
                              '&:hover': {
                                backgroundColor: '#8b003c', // keep background same
                                color: 'white',             // prevent text turning blue
                              },         // prevent text turning blue
                            },}} 
                          >
                            {action.label}
                          </Button>
                        ))}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalLength}
        page={currentPage}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        sx={{
          padding: "16px",
          ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
            fontSize: "14px",
          },
        }}
      />
    </div>
  );
};

export default CustomTable;
